import { Dispatch, SetStateAction } from "react";
import {
  DateRangePreset,
  DateRangeValue,
  formatDateForApi,
  getPresetDateRange,
  startOfDay,
} from "../utils/dateRangeFilters";
import { usePersistedState } from "./usePersistedState";

type PersistedDateRange = {
  preset: DateRangePreset;
  from?: string;
  to?: string;
};

const DATE_RANGE_PRESETS = new Set<DateRangePreset>([
  "all_time",
  "today",
  "yesterday",
  "last_7_days",
  "last_30_days",
  "this_month",
  "last_month",
  "custom",
]);

const isDateRangePreset = (value: unknown): value is DateRangePreset =>
  typeof value === "string" && DATE_RANGE_PRESETS.has(value as DateRangePreset);

const parseApiDate = (value: unknown): Date | null => {
  if (typeof value !== "string") return null;

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : startOfDay(date);
};

const serializeDateRange = (value: DateRangeValue) =>
  JSON.stringify({
    preset: value.preset,
    from: formatDateForApi(value.from),
    to: formatDateForApi(value.to),
  } satisfies PersistedDateRange);

const deserializeDateRange = (
  raw: string,
  fallback: DateRangeValue,
): DateRangeValue => {
  const parsed = JSON.parse(raw) as Partial<PersistedDateRange>;
  if (!isDateRangePreset(parsed.preset)) {
    return fallback;
  }

  if (parsed.preset !== "custom") {
    return getPresetDateRange(parsed.preset);
  }

  const from = parseApiDate(parsed.from);
  const to = parseApiDate(parsed.to);
  if (!from || !to || from.getTime() > to.getTime()) {
    return fallback;
  }

  return { preset: "custom", from, to };
};

export const usePersistedDateRange = (
  sectionKey: string,
  defaultPreset: Exclude<DateRangePreset, "custom"> = "last_30_days",
): [DateRangeValue, Dispatch<SetStateAction<DateRangeValue>>] =>
  usePersistedState<DateRangeValue>(
    sectionKey,
    () => getPresetDateRange(defaultPreset),
    {
      serialize: serializeDateRange,
      deserialize: deserializeDateRange,
    },
  );
